import { FC, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useRegisterProductsMutation } from 'store/productService';
import { displayError } from 'store/globalErrorState';
import { customerSelector } from 'store/customerState';
import * as routes from 'router/routes';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useTranslations } from 'hooks/useTranslations';
import { parseJsonError } from 'utils/fetchUtils';
import { pushGTMEvent } from 'utils/analyticsUtils';

import PageWrapper from 'components/UI/Page/PageWrapper';
import PageState from 'components/UI/Page/PageState';
import Loader from 'components/UI/Loader';
import Button from 'components/UI/Buttons/Button';
import { IconType } from 'components/UI/Icon';

import { ButtonGrid } from './styled';
import {
  completeProductRegistrationFailure,
  completeProductRegistrationStart,
  completeProductRegistrationSuccess,
  productRegistrationError,
  productRegistrationSelector,
  resetProductRegistrationState
} from 'store/productRegistrationState';

const RegisterProductsPage: FC = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Redux
  const { productInfo, soldDate, isCompleted, hasErrors } = useAppSelector(
    productRegistrationSelector
  );
  const { customer } = useAppSelector(customerSelector);

  // Hooks
  const [registerProducts, { isSuccess, error, isLoading }] =
    useRegisterProductsMutation();

  // Register products
  const onRegisterProducts = useCallback(() => {
    if (customer && productInfo && soldDate) {
      registerProducts({
        customer,
        product: productInfo,
        soldDate
      });
    }
  }, [registerProducts, customer, productInfo, soldDate]);

  // Register products
  useEffect(() => {
    onRegisterProducts();
  }, [onRegisterProducts, productInfo]);

  // Handle success
  useEffect(() => {
    // If not successful or no product info
    if (!isSuccess || !productInfo) {
      return;
    }

    // Complete registration and display success toast
    dispatch(completeProductRegistrationSuccess());
    dispatch(displayError('product.registered.success'));

    // Track success event for analytics
    pushGTMEvent('productRegistrationSuccess', {
      elementInfo: {
        value:
          productInfo.productno ||
          productInfo.pnc ||
          productInfo.husqvarnaId ||
          ''
      }
    });
  }, [dispatch, isSuccess, productInfo]);

  // Handle error
  useEffect(() => {
    if (error) {
      const { data } = error as any;
      const apiError = parseJsonError(data);
      dispatch(productRegistrationError(apiError));

      // Track error event for analytics
      pushGTMEvent('productRegistrationError', {
        elementInfo: {
          text: apiError.message,
          value:
            productInfo?.productno ||
            productInfo?.pnc ||
            productInfo?.husqvarnaId ||
            ''
        }
      });
    }
  }, [dispatch, error, productInfo]);

  // Redirect on success
  useEffect(() => {
    if (isSuccess || !productInfo || isCompleted) {
      dispatch(resetProductRegistrationState());
      navigate(routes.PRODUCTS);
    }
  }, [dispatch, navigate, isSuccess, productInfo, isCompleted]);

  // Navigate to products
  const onGoToHome = useCallback(() => {
    dispatch(completeProductRegistrationFailure());
    navigate(routes.PRODUCTS);
  }, [dispatch, navigate]);

  // Retry registration
  const onRetryRegistration = useCallback(() => {
    dispatch(completeProductRegistrationStart());
    onRegisterProducts();
  }, [dispatch, onRegisterProducts]);

  // Retry if registration fails
  if (hasErrors && !isLoading) {
    return (
      <PageWrapper>
        <PageState
          iconType={IconType.Error}
          title={t('finalizeproductregistration.error.title')}
          description={t('finalizeproductregistration.error.description')}
        >
          <ButtonGrid>
            <Button onClick={onRetryRegistration}>
              {t('finalizeproductregistration.error.btn')}
            </Button>
            <Button
              color="transparent"
              textColor="primary"
              onClick={onGoToHome}
            >
              {t('not_found.btn_to_home')}
            </Button>
          </ButtonGrid>
        </PageState>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Loader center size="large" />
    </PageWrapper>
  );
};

export default RegisterProductsPage;
